import { ArrowDropDown } from '@mui/icons-material';
import { Button, InputAdornment, Menu, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { CALENDAR_VIEW, NUMBER_OF_MONTH, SLICE_NAME } from 'constants/common';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/vi';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import { useMenuCC } from 'hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from 'theme';
import { updateCurrentDay } from 'pages/manager-meeting/managerMeetingSlice';
import IconCalendarDatePicker from '../DateRangePickerCC/IconCalendar';

dayjs.extend(isBetweenPlugin);

const CustomPickersDay = styled(PickersDay, {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isHovered',
})(({ theme, isSelected, isHovered, day }) => ({
    borderRadius: '50% !important',
    padding: '0 !important',
    margin: '0 2px !important',
    ...(isSelected && {
        backgroundColor: color.greyIL07,
        // color: theme.palette.primary.contrastText,
        '&:hover, &:focus': {
            // backgroundColor: theme.palette.primary.A50,
        },
    }),
    ...(isHovered && {
        backgroundColor: color.greyIL07,
        '&:hover, &:focus': {
            // backgroundColor: theme.palette.primary[theme.palette.mode],
        },
    }),
    ...(day.day() === 0 && {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
    }),
    ...(day.day() === 6 && {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
    }),
}));

const isInSameWeek = (dayA, dayB) => {
    if (dayB == null) {
        return false;
    }

    return dayA.isSame(dayB, 'week');
};

function Day(props) {
    const { day, selectedDay, hoveredDay, isChooseWeek = false, ...other } = props;

    if (isChooseWeek) {
        return (
            <CustomPickersDay
                {...other}
                day={day}
                sx={{ px: 2.5 }}
                disableMargin
                selected={false}
                isSelected={isInSameWeek(day, selectedDay)}
                isHovered={isInSameWeek(day, hoveredDay)}
            />
        );
    }

    return (
        <CustomPickersDay
            {...other}
            day={day}
            sx={{ px: 2.5 }}
            disableMargin
            selected={false}
        />
    )
}
const getStartTime = (type, value) => {
    if (type === CALENDAR_VIEW.TIME_GRID_DAY) return dayjs(value).startOf('day').unix() * 1000
    return dayjs(value).day(0).startOf('day').unix() * 1000
}

const getEndTime = (type, value) => {
    if (type === CALENDAR_VIEW.TIME_GRID_DAY) return dayjs(value).endOf('day').unix() * 1000
    return dayjs(value).day(6).endOf('day').unix() * 1000
}

export default function WeekPicker({ setDiffTime, type = CALENDAR_VIEW.TIME_GRID_WEEK, handleChangeTimeInCalendar }) {
    const [hoveredDay, setHoveredDay] = useState(null);
    const { open, anchorEl, setAnchorEl } = useMenuCC()
    const { currentDay } = useSelector(state => state[SLICE_NAME.MANAGER_MEETING])
    const dispatch = useDispatch()
    const { i18n } = useTranslation()
    const [timeValue, setTimeValue] = useState(() => {
        return {
            start: getStartTime(type, currentDay), 
            end: getEndTime(type, currentDay)
        }
    })

    const isMonth = (numMonth) => {
        return i18n.t(NUMBER_OF_MONTH[numMonth])
    }

    const handleHideWeekPicker = (newValue, selectionState, type) => {
        dispatch(updateCurrentDay(newValue))
        if (selectionState === "finish") {
            handleChangeTimeInCalendar(type, '', getStartTime(type, newValue), getEndTime(type, newValue))
            setAnchorEl(null)
            setDiffTime((prev) => {
                return {
                    ...prev,
                    diff: type === CALENDAR_VIEW.TIME_GRID_DAY ? Math.round(dayjs(newValue).diff(prev.current, 'day', true)) : Math.round(dayjs(newValue).diff(prev.current, 'week', true))
                }
            })
            setTimeValue({
                start: getStartTime(type, newValue), 
                end: getEndTime(type, newValue)
            })
        }
    }

    useEffect(() => {
        setTimeValue({
            start: getStartTime(type, currentDay), 
            end: getEndTime(type, currentDay)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDay])

    return (
        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterDayjs}>
            {false && <Button
                variant='text'
                id="lang-more"
                aria-controls={open ? 'lang-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<ArrowDropDown />}
            >
                {isMonth(dayjs(currentDay).month())} - {dayjs(currentDay).year()}
            </Button>}
            {timeValue && <TextField sx={{ mx: 2, backgroundColor: color.white }} onClick={(e) => setAnchorEl(e.currentTarget)} value={`${dayjs.unix(timeValue.start/1000).format('DD/MM/YYYY')} - ${dayjs.unix(timeValue.end/1000).format('DD/MM/YYYY')}`} InputProps={{
                endAdornment: (
                    <InputAdornment  sx={{ cursor: 'pointer' }} position="end">
                        <IconCalendarDatePicker />
                    </InputAdornment>
                ),
            }}/>}
            <Menu
                id="lang-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{
                    'aria-labelledby': 'lang-more',
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {type === CALENDAR_VIEW.TIME_GRID_DAY ? <DateCalendar
                    value={dayjs(currentDay)}
                    onChange={(newValue, selectionState) => handleHideWeekPicker(newValue, selectionState, type)}
                    showDaysOutsideCurrentMonth
                    slots={{ day: Day }}
                    slotProps={{
                        day: (ownerState) => ({
                            selectedDay: currentDay,
                            hoveredDay,
                            onPointerEnter: () => setHoveredDay(ownerState.day),
                            onPointerLeave: () => setHoveredDay(null),
                        })
                    }}
                    dayOfWeekFormatter={(_day, weekday) => `${weekday.locale(i18n.language).format('dd')}.`}
                /> : <DateCalendar
                    value={dayjs(currentDay)}
                    onChange={(newValue, selectionState) => handleHideWeekPicker(newValue, selectionState, type)}
                    showDaysOutsideCurrentMonth
                    slots={{ day: Day }}
                    slotProps={{
                        day: (ownerState) => ({
                            selectedDay: currentDay,
                            hoveredDay,
                            onPointerEnter: () => setHoveredDay(ownerState.day),
                            onPointerLeave: () => setHoveredDay(null),
                        })
                    }}
                    dayOfWeekFormatter={(_day, weekday) => `${weekday.locale(i18n.language).format('dd')}.`}
                />}
            </Menu>
        </LocalizationProvider>
    );
}
